// 负责轻量店主后台 UI，与后台 API、订单履约和商品库存管理关联。
import { useEffect, useMemo, useState } from "react";
import {
  fetchAdminOrders,
  fetchAdminProducts,
  updateAdminOrderFulfillment,
  updateAdminProductVariant,
  type AdminOrder,
  type AdminProduct,
  type AdminProductVariant
} from "../../app/adminApi";
import { formatCurrency } from "../../domain/cart";
import type { FulfillmentStatus } from "../../domain/orders";

const tokenStorageKey = "clothing-ecom-admin-token";

const fulfillmentStatuses: FulfillmentStatus[] = [
  "unfulfilled",
  "processing",
  "shipped",
  "delivered",
  "returned"
];

export const AdminView = () => {
  const [tokenInput, setTokenInput] = useState(
    () => sessionStorage.getItem(tokenStorageKey) ?? ""
  );
  const [token, setToken] = useState(
    () => sessionStorage.getItem(tokenStorageKey) ?? ""
  );
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const selectedOrder = useMemo(() => {
    return orders.find((order) => order.id === selectedOrderId) ?? orders[0];
  }, [orders, selectedOrderId]);

  useEffect(() => {
    if (token) {
      void loadAdminData(token);
    }
  }, [token]);

  // 使用当前输入令牌登录后台并加载数据。
  const signIn = () => {
    const trimmedToken = tokenInput.trim();

    if (!trimmedToken) {
      setError("Admin token is required.");
      return;
    }

    sessionStorage.setItem(tokenStorageKey, trimmedToken);
    setToken(trimmedToken);
  };

  // 退出后台并清空本地令牌。
  const signOut = () => {
    sessionStorage.removeItem(tokenStorageKey);
    setToken("");
    setTokenInput("");
    setOrders([]);
    setProducts([]);
    setSelectedOrderId("");
  };

  // 拉取后台订单和商品数据。
  const loadAdminData = async (activeToken = token) => {
    setIsLoading(true);
    setError("");
    setMessage("");

    try {
      const [nextOrders, nextProducts] = await Promise.all([
        fetchAdminOrders(activeToken),
        fetchAdminProducts(activeToken)
      ]);
      setOrders(nextOrders);
      setProducts(nextProducts);
      setSelectedOrderId((currentOrderId) => currentOrderId || nextOrders[0]?.id || "");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Admin load failed.");
    } finally {
      setIsLoading(false);
    }
  };

  // 更新订单履约状态。
  const updateFulfillment = async (
    orderId: string,
    fulfillmentStatus: FulfillmentStatus
  ) => {
    setIsLoading(true);
    setError("");
    setMessage("");

    try {
      const updatedOrder = await updateAdminOrderFulfillment(
        token,
        orderId,
        fulfillmentStatus
      );
      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === updatedOrder.id ? updatedOrder : order
        )
      );
      setMessage(`Order ${updatedOrder.id} updated.`);
    } catch (updateError) {
      setError(
        updateError instanceof Error
          ? updateError.message
          : "Fulfillment update failed."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // 在本地更新商品变体字段，等待用户保存。
  const updateLocalVariant = (
    variant: AdminProductVariant,
    nextVariant: AdminProductVariant
  ) => {
    setProducts((currentProducts) =>
      currentProducts.map((product) =>
        product.id === variant.productId
          ? {
              ...product,
              variants: product.variants.map((candidate) =>
                candidate.size === variant.size ? nextVariant : candidate
              )
            }
          : product
      )
    );
  };

  // 保存商品变体库存和上下架状态。
  const saveVariant = async (variant: AdminProductVariant) => {
    setIsLoading(true);
    setError("");
    setMessage("");

    try {
      setProducts(await updateAdminProductVariant(token, variant));
      setMessage(`${variant.productId} / ${variant.size} saved.`);
    } catch (updateError) {
      setError(
        updateError instanceof Error ? updateError.message : "Variant update failed."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="admin-shell">
        <section className="admin-login" aria-label="Admin sign in">
          <h1>Store admin</h1>
          <label>
            <span>Admin token</span>
            <input
              type="password"
              value={tokenInput}
              onChange={(event) => setTokenInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  signIn();
                }
              }}
            />
          </label>
          <button className="primary-button" type="button" onClick={signIn}>
            Sign in
          </button>
          {error ? <p className="setup-note error">{error}</p> : null}
        </section>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <header className="admin-topbar">
        <div>
          <p>Store admin</p>
          <h1>Orders and inventory</h1>
        </div>
        <div>
          <button type="button" onClick={() => void loadAdminData()}>
            Refresh
          </button>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      {error ? <p className="admin-alert error">{error}</p> : null}
      {message ? <p className="admin-alert">{message}</p> : null}
      {isLoading ? <p className="admin-alert">Loading...</p> : null}

      <section className="admin-grid">
        <section className="admin-panel" aria-label="Orders">
          <div className="admin-panel-heading">
            <h2>Orders</h2>
            <span>{orders.length}</span>
          </div>
          <div className="admin-order-list">
            {orders.map((order) => (
              <button
                className={selectedOrder?.id === order.id ? "active" : ""}
                type="button"
                key={order.id}
                onClick={() => setSelectedOrderId(order.id)}
              >
                <strong>{order.id}</strong>
                <span>{order.customerEmail}</span>
                <span>
                  {order.paymentStatus} / {order.fulfillmentStatus}
                </span>
                <span>{formatCurrency(order.totalAmount)}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="admin-panel" aria-label="Order detail">
          <div className="admin-panel-heading">
            <h2>Order detail</h2>
          </div>
          {selectedOrder ? (
            <div className="admin-detail">
              <div className="admin-fields">
                <span>{selectedOrder.customerEmail}</span>
                <span>{selectedOrder.shippingCountry}</span>
                <span>{new Date(selectedOrder.createdAt).toLocaleString()}</span>
              </div>
              <label>
                <span>Fulfillment</span>
                <select
                  value={selectedOrder.fulfillmentStatus}
                  onChange={(event) =>
                    void updateFulfillment(
                      selectedOrder.id,
                      event.target.value as FulfillmentStatus
                    )
                  }
                >
                  {fulfillmentStatuses.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
              </label>
              <div className="admin-lines">
                {selectedOrder.lines.map((line) => (
                  <div key={`${line.productId}-${line.size}`}>
                    <span>
                      {line.productName} / {line.size} x {line.quantity}
                    </span>
                    <strong>{formatCurrency(line.lineTotalAmount)}</strong>
                  </div>
                ))}
              </div>
              <div className="admin-lines">
                {selectedOrder.paymentEvents.map((event) => (
                  <div key={event.providerEventId}>
                    <span>{event.providerEventId}</span>
                    <strong>{event.outcome}</strong>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="empty-cart">No orders yet.</p>
          )}
        </section>

        <section className="admin-panel admin-products" aria-label="Products">
          <div className="admin-panel-heading">
            <h2>Products</h2>
            <span>{products.length}</span>
          </div>
          {products.map((product) => (
            <article className="admin-product" key={product.id}>
              <img src={product.imageUrl} alt={product.name} />
              <div>
                <h3>{product.name}</h3>
                <span>{product.category}</span>
              </div>
              <div className="admin-variants">
                {product.variants.map((variant) => (
                  <div className="admin-variant" key={`${product.id}-${variant.size}`}>
                    <strong>{variant.size}</strong>
                    <span>{variant.color}</span>
                    <span>{formatCurrency(variant.priceAmount)}</span>
                    <input
                      type="number"
                      min="0"
                      value={variant.stockQuantity}
                      onChange={(event) =>
                        updateLocalVariant(variant, {
                          ...variant,
                          stockQuantity: Number(event.target.value)
                        })
                      }
                    />
                    <label className="admin-checkbox">
                      <input
                        type="checkbox"
                        checked={variant.isActive}
                        onChange={(event) =>
                          updateLocalVariant(variant, {
                            ...variant,
                            isActive: event.target.checked
                          })
                        }
                      />
                      <span>Active</span>
                    </label>
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => void saveVariant(variant)}
                    >
                      Save
                    </button>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </section>
      </section>
    </main>
  );
};
